import ERROR_CODE from 'src/constants/ErrorCodes';
import { STAGE } from 'src/constants/StageConfig';

/**
 * Constant object to store paths for all pages
 */
export const PAGE_PATHS = {
  HOME: '/',
  HOME_PAGE: '/home',
  ACTION_CENTER: '/actionCenter',
  VIEW_EMPLOYEE_DETAILS: '/employeeDirectory/:concernedEmployeeIdentifier/details',
  VIEW_EXTERNAL_CONTACT_INFO: '/externalContactInfo/:externalContactId/details',
  CREATE_OPSHR_CASE_WF: '/employeeDirectory/:concernedEmployeeId/:concernedEmployeeAlias/details',
  CASE_LIST: '/caseList/',
  EXTERNAL_CONTACT_DIRECTORY: '/externalDirectory/',
  TRANSCRIBE_INTERVIEW: '/caseDetails/:useCase/:caseId/interview/:interviewNumber/transcribe',
  CASE_DETAILS: '/caseDetails/:useCase/:caseId',
  CASE_SEARCH_PAGE: '/caseSearchResult',
  TASK_DETAILS: '/taskDetails/:useCase/:taskId',
  CREATE_CASE: '/createCase/:useCase/:concernedEmployeeId',
  EMPLOYEE_SEARCH_PAGE: '/employeeDirectory',
  CONCERNED_ENTITY_CASES: '/concernedEntityCases',
  MESSAGE_BOARD_PAGE: '/messageBoard',
  CUBE_NEWS_HOME_PAGE: '/news',
  CUBE_NEWS_DETAIL_PAGE: '/news/content/:newsId',
  WORKFLOW: '/workflow',
  WORKFLOW_STEP: '/workflow',
  NUPT_EMPLOYEE_INFO: '/nuptEmployeeInfo',
  FAILURE_PAGE: '/error/:errorCode',
  FAILURE_PAGE_ROOT: '/error/',
  TCUI_SPOOFED_USER: '?runas=brest',
  ACCESS_DENIED: '/accessDenied',
  UNAUTHORIZED_ACCESS: '/unauthorizedAccess',
  CASE_CREATION_COMPLETED: '/caseCreationCompleted',
  CLONE_CASE: '/cloneCase/:useCase/:concernedEmployeeId/:caseId',
  FEDERATE_LOGIN: '/login',
  IDP_RESPONSE: '/idpresponse',
  TERMINATION_VALIDATION_SESSION: '/terminationValidation/session',
  COE_SUPPORT_CASE_DETAILS: '/support/supportCaseDetails/:useCase/:caseId',
  SUPPORT: '/support',
  INSIGHTS: '/insights',
  HR_INSIGHTS: '/hrInsights',
  OUT_OF_OFFICE: '/outOfOffice',
  AUTO_WORK_ASSIGNMENT: '/autoWorkAssignment',
  SITE_WALK_UP: '/siteWalkUp',
  SITE_WALK_UP_CREATE_CASE: '/siteWalkUp/createCase/:concernedAlias',
  SITE_WALK_UP_SUPPORT: '/siteWalkUp/support',
  SITE_WALK_UP_SUPPORT_CONTENT: '/siteWalkUp/support/content',
  MYTIME_REPORTING: '/myTimeReporting',
  SCHEDULE_AUDIT: '/scheduleAudit',
  STATUS_HISTORY: '/statusHistory',
  STATUS_HISTORY_TO_REQUEST_DETAILS: '/statusHistory/:requestId',
  HAND_OFF_PAGE: '/handOff',
  CREATE_SEVERANCE_PAGE: '/terminations/severances/:employeeId',
  SEVERANCE_PAGE: '/terminations/severances/:employeeId/:severanceId',
  INITIATE_SEVERANCE_PAGE: '/terminations/severances',
  SITE_CLOSURE: '/siteClosure',
  EXPO_SCHEDULE_REQUEST: '/expoRequest',
  EXPO_SCHEDULE_REQUEST_SUCCESS: '/expoRequestSuccess',
  ASK_HRSC: '/askHRSC',
  TEAM_PREFERENCE: '/teamPreferences',
  USER_PREFERENCE: '/userPreferences',
  LEAVE_STATUS_FEED: '/LeaveStatusFeed/:employeeAlias/:employeeId/:employeeFullName',
  TERMINATION_APPROVAL: '/terminationApproval/:caseId/:actionId',
  FEEDBACK_DETAILS: '/employeeDirectory/feedback/:employeeId/feedbackdetails',
  VIEW_EXTERNAL_CONTACT_PAGE: '/externalContactInfo',
  // Consent Portal Routes
  CONSENT_GUEST: '/consent',
  CONSENT_AUTHENTICATED: '/consent/authenticated',
  AGENTIC_WORKFLOWS: '/agenticWorkflows',
  AGENT_SKILLS: '/agentSkills',
  WFR_CREATE_PROJECT: '/workforceReduction/projects/new',
  WFR_PROJECT_DETAILS: '/workforceReduction/projects/:projectId',
  ADAPT_FEEDBACK_DETAIL: '/adaptFeedbackDetail',
  ALL: '*',
};

/**
 * Enum to store path route names
 */
export const PATH_ROUTE_NAMES = {
  CASE_DETAILS: 'caseDetails',
  TASK_DETAILS: 'taskDetails',
};

/**
 * Constants Objcet to store Page Names
 */
export const PAGE_NAMES = {
  HOME: '',
  ACTION_CENTER: 'Action Center',
  EMPLOYEE_DIRECTORY: 'Employee Directory',
  NUPT_EMPLOYEE_INFO: 'NUPT Employee Info',
  VIEW_EMPLOYEE_DETAILS: 'Employee Details',
  VIEW_EXTERNAL_CONTACT_INFO: 'External Contact Detail',
  CASE_DETAILS: 'Case Details',
  CASE_LIST: 'Case List',
  EXTERNAL_DIRECTORY: 'External Contact Directory',
  CASE_SEARCH: 'Case Search',
  OOO_PROXY_LIST: 'OOO List',
  ACCESS_DENIED: 'Access Denied',
  PAGE_NOT_FOUND: 'Page not found',
  CASE_CREATION_COMPLETED: 'Case Creation Completed',
  TASK_DETAILS: 'Task Details',
  SITE_WALK_UP: 'Site walk-up',
  CREATE_CASE_WALKUP: 'Record walk-up',
  WALKUP_SUPPORT: 'Provide support',
  MYTIME_REPORTING: 'MyTime Reporting',
  PAGE_NOT_AVAILABLE: 'Page Not Available',
  HAND_OFF_PAGE: 'Hand off cases page',
  SEVERANCE_PAGE: 'Severance',
  INITIATE_SEVERANCE_PAGE: 'Initiate Severance',
  MESSAGE_BOARD: 'Message Board',
  HR_INSIGHTS: 'HR Insights',
  ASK_HRSC: 'Ask HRSC',
  CUBE_NEWS: 'Cube',
  LEAVE_STATUS_FEED: 'Leave Status Feed',
  TERMINATION_APPROVAL: 'Termination Approval',
  // Consent Portal Page Names
  CONSENT_GUEST: 'Consent Portal',
  CONSENT_AUTHENTICATED: 'Authenticated Consent Portal',
};

/**
 * Mapping from Error message to Error code.
 */
//TODO: ADD OTHER ERROR MAPPING WHEN REQUIRED
export const GLOBAL_ERROR_MESSAGE_TO_ERROR_CODE: Record<ERROR_CODE, string> = {
  [ERROR_CODE.SITE_FAILURE]: '5xx',
  [ERROR_CODE.WORKFLOW_FAILURE]: '5xx',
  [ERROR_CODE.WORKFLOW_TIMEOUT]: '5xx',
  [ERROR_CODE.EMPLOYEE_NOT_FOUND]: '404',
  [ERROR_CODE.PXT_ONE_CASE_ACCESS_ERROR]: '403',
  [ERROR_CODE.ATP_CASE_ACCESS_ERROR]: '403',
};

/**
 * Constant object to store endpoints for all APIs
 */
export const API_ENDPOINTS = {
  // Core Service Endpoints
  ADD_RECENTLY_VIEWED_ITEM: '/api/hrxCoreService/add-recently-viewed-item',
  GET_APPLICABLE_WIDGETS: '/api/hrxCoreService/get-applicable-widgets',
  GET_FEDERATE_TOKENS: '/api/hrxCoreService/get-federate-tokens',
  GET_NOTIFICATIONS_FOR_CASES: '/api/hrxCoreService/get-notifications-for-cases',
  GET_PUNCH_CORRECTION_VIEW_WIDGETS: '/api/hrxCoreService/get-punch-correction-view-widgets',
  GET_RECENTLY_VIEWED_ITEMS: '/api/hrxCoreService/get-recently-viewed-items',
  GET_SECRET: '/api/hrxCoreService/get-secret',
  UPDATE_NOTIFICATIONS_AS_READ: '/api/hrxCoreService/update-notifications-as-read',

  // Plugin Service Endpoints
  ADD_OR_UPDATE_CORRESPONDENCE: '/api/hrxPluginService/add-or-update-correspondence',
  ADD_OR_UPDATE_NOTES: '/api/hrxPluginService/add-or-update-notes',
  CANCEL_TERMINATION_VALIDATION_FORM: '/api/hrxPluginService/cancel-termination-validation-form',
  CONVERT_TEXT_TO_JSON_REQUEST: '/api/hrxPluginService/convert-text-to-json',
  CREATE_CASE: '/api/hrxPluginService/create-case',
  CREATE_HRP_OOO: '/api/hrxPluginService/create-hrp-ooo',
  CREATE_PACKET_V2: '/api/hrxPluginService/create-appeal-packet-v2',
  CREATE_CUSTOM_OBJECTS: '/api/hrxPluginService/create-custom-objects',
  DELETE_CASE_ADDITIONAL_DETAILS: '/api/hrxPluginService/delete-case-additional-details',
  DELETE_CUSTOM_OBJECT: '/api/hrxPluginService/delete-custom-object',
  DELETE_ASSET: '/api/hrxPluginService/delete-asset',
  DELETE_EXACT_RECORD: '/api/hrxPluginService/delete-exact-record',
  GET_EXACT_LEAVE_OF_ABSENCE: '/api/hrxPluginService/get-exact-leave-of-absence',
  UPSERT_EXACT_LEAVE_OF_ABSENCE: '/api/hrxPluginService/upsert-exact-leave-of-absence',
  DELETE_HRP_OOO: '/api/hrxPluginService/delete-hrp-ooo',
  EXECUTE_KHONSU_OPERATION: '/api/hrxPluginService/execute-khonsu-operation',
  EXECUTE_MYHR_GATEWAY_OPERATION: '/api/hrxPluginService/execute-myhr-gateway-operation',
  EXECUTE_PLUGIN: '/api/hrxPluginService/execute-plugin',
  GET_ACCOMMODATION_DECISION_REQUEST: '/api/hrxPluginService/get-dls-decision-requests',
  GET_APPCONFIG_DATA: '/api/hrxPluginService/get-appconfig-data',
  GET_ATTACHMENT_DOWNLOAD_URL: '/api/hrxPluginService/get-attachment-download-url',
  GET_ATTACHMENT_UPLOAD_URL: '/api/hrxPluginService/get-attachment-upload-url',
  GET_ATTACHMENT_URL: '/api/hrxPluginService/get-attachment',
  GET_ATTENDANCE_BALANCES: '/api/hrxPluginService/get-attendance-balances',
  GET_ATTENDANCE_STATUS: '/api/hrxPluginService/get-attendance-status',
  GET_BALANCES: '/api/hrxPluginService/get-balances',
  GET_BULK_EMPLOYEE_DETAILS: '/api/hrxPluginService/get-employees',
  GET_ALL_CASE_DETAILS: '/api/hrxPluginService/get-all-case-details',
  GET_CASE: '/api/hrxPluginService/get-case',
  GET_CASE_ADDITIONAL_DETAILS: '/api/hrxPluginService/get-case-additional-details',
  GET_CUSTOM_OBJECTS: '/api/hrxPluginService/get-custom-objects',
  GET_CUSTOM_OBJECT: '/api/hrxPluginService/get-custom-object',
  GET_CASE_SUMMARY: '/api/hrxPluginService/get-case-summary',
  GET_CASES: '/api/hrxPluginService/get-cases',
  GET_CASES_ADDITIONAL_DETAILS: '/api/hrxPluginService/get-cases-additional-details',
  GET_CONTACT_ALERTS: '/api/hrxPluginService/get-contact-alerts',
  GET_EMAIL_BODY_DETAILS: '/api/hrxPluginService/get-email-body-details',
  GET_EMAIL_TEMPLATES: '/api/hrxPluginService/get-email-templates',
  GET_EMPLOYEE_DETAILS: '/api/hrxPluginService/get-employee-details',
  GET_EMPLOYEE_SUPPORTING_HRS: '/api/hrxPluginService/get-employee-supporting-hrs',
  GET_EMPLOYEE_TICKETS: '/api/hrxPluginService/get-employee-tickets',
  GET_EMPLOYEES_SUPPORTED_BY_HR: '/api/hrxPluginService/get-employees-supported-by-hr',
  GET_ENTITY_DETAILS: '/api/hrxPluginService/get-entity-details',
  GET_FEEDBACK_CASES_BY_EMPLOYEE_ID: '/api/hrxPluginService/get-feedback-cases-by-employee-id',
  GET_FEEDBACK_HISTORY: '/api/hrxPluginService/get-feedback-history',
  GET_INCIDENT: '/api/hrxPluginService/get-incident',
  GET_HR_BANNER: '/api/hrxPluginService/get-hr-banner',
  GET_HRP_OOO: '/api/hrxPluginService/get-hrp-ooo',
  GET_HRP_OOO_RECORDS: '/api/hrxPluginService/get-hrp-ooo-records',
  GET_INSIGHTS_S3_PRESIGNED_URL: '/api/hrxPluginService/get-insights-s3-presigned-url',
  GET_IVY_TASKS: '/api/hrxPluginService/get-ivy-tasks',
  GET_KDS_CONTENT: '/api/hrxPluginService/search-content',
  GET_NEWS: '/api/hrxPluginService/get-news',
  GET_CUBE_NEWS_FEED: '/api/hrxPluginService/get-cube-news-feed',
  GET_CUBE_NEWS_BANNER_FEED: '/api/hrxPluginService/get-cube-news-banner-feed',
  GET_CUBE_NEWS_DETAIL: '/api/hrxPluginService/get-cube-news-detail',
  GET_LLM_RESULT: '/api/hrxPluginService/get-llm-result',
  GET_OPS_CONTACT_DRIVERS: '/api/hrxPluginService/get-ops-contact-drivers',
  GET_PACKET: '/api/hrxPluginService/get-appeal-packet',
  GET_PACKET_V2: '/api/hrxPluginService/get-appeal-packet-v2',
  GET_PAYCODES: '/api/hrxPluginService/get-paycodes',
  GET_PERSON_ID_FROM_BADGE_ID: '/api/hrxPluginService/get-person-id-from-badge-id',
  GET_PI_ENGAGE_DETAILS: '/api/hrxPluginService/get-pi-engage-details',
  GET_PUNCHES: '/api/hrxPluginService/get-punches',
  GET_QUEUES: '/api/hrxPluginService/get-queues',
  GET_QUICK_TEXTS: '/api/hrxPluginService/get-quick-texts',
  GET_ROUTING_CONFIGURATIONS: '/api/hrxPluginService/get-routing-destinations',
  GET_SHIFTS: '/api/hrxPluginService/get-shifts',
  GET_TIMECARD: '/api/hrxPluginService/get-time-card',
  GET_SHIFT_PATTERN_DETAILS: '/api/hrxPluginService/get-shift-pattern-details',
  GET_STATIC_CONTENT: '/api/hrxPluginService/get-static-content',
  GET_TASK: '/api/hrxPluginService/get-task',
  GET_TERMINATION_METADATA: '/api/hrxPluginService/get-termination-metadata',
  GET_TEXT_WITH_MERGE_FIELDS_RESOLVED: '/api/hrxPluginService/get-text-with-merge-fields-resolved',
  GET_UNIFIED_DATA: '/api/hrxPluginService/get-employee-unified-data',
  GET_USERS: '/api/hrxPluginService/get-users',
  GET_WALK_UP_SUPPORT_INFO: '/api/hrxPluginService/get-walk-up-support-info',
  GET_WORKER_COMP_CASES: '/api/hrxPluginService/get-worker-compensation-cases',
  GET_WORKFLOW_ID: '/api/hrxPluginService/get-workflow-id',
  GET_HANDOFF_DESTINATION: '/api/hrxPluginService/get-handoff-destination',
  HANDOFF_CASE: '/api/hrxPluginService/handOff-case',
  INVOKE_PROMPT_FLOW: '/api/hrxPluginService/invoke-prompt-flow',
  MANAGE_WATCHERS: '/api/hrxPluginService/manage-watchers',
  PUSH_NOTIFICATION: '/api/hrxPluginService/push-notification',
  REFRESH_PACKET_DOMAIN_V2: '/api/hrxPluginService/refresh-appeal-domain-v2',
  REFRESH_PACKET_V2: '/api/hrxPluginService/refresh-appeal-packet-v2',
  RESEND_APPEAL_EMAIL: '/api/hrxPluginService/resend-appeal-email',
  SAVE_PACKET: '/api/hrxPluginService/save-appeal-packet',
  SAVE_PACKET_V2: '/api/hrxPluginService/save-appeal-packet-v2',
  SAVE_APPEAL_PACKET_DOCUMENT: '/api/hrxPluginService/save-appeal-packet-document',
  COMMIT_EDM_APPEAL_PACKET: '/api/hrxPluginService/commit-edm-appeal-packet',
  SEARCH_DEVIATIONS: '/api/hrxPluginService/search-deviations',
  SEARCH_EMPLOYEE: '/api/hrxPluginService/search-employee',
  GET_ANOMALIES: '/api/hrxPluginService/get-anomalies',
  CREATE_EXTERNAL_CONTACT: '/api/hrxPluginService/create-external-contact',
  GET_CONTACT: '/api/hrxPluginService/get-contact',
  SEARCH_CONTACT: '/api/hrxPluginService/search-contact',
  SEND_EMAIL: '/api/hrxPluginService/send-email',
  SUBMIT_TERMINATION_VALIDATION_FORM: '/api/hrxPluginService/submit-termination-validation-form',
  UPDATE_CASE: '/api/hrxPluginService/update-case',
  UPDATE_CASES: '/api/hrxPluginService/update-cases',
  UPDATE_HRP_OOO: '/api/hrxPluginService/update-hrp-ooo',
  UPDATE_IMPERSONATION: '/api/hrxPluginService/update-impersonation',
  UPDATE_TASK: '/api/hrxPluginService/update-task',
  UPDATE_TASKS: '/api/hrxPluginService/update-tasks',
  UPDATE_CUSTOM_OBJECT: '/api/hrxPluginService/update-custom-object',
  UPLOAD_IMAGE: '/api/hrxPluginService/upload-image',
  WORKFLOW_UI_BASE: '/api/hrxPluginService/workflow-ui-base',
  GET_LOCATION_BY_ID: '/api/hrxPluginService/get-location-by-id',
  CREATE_AWA_PREFERENCE: '/api/hrxPluginService/create-awa-preference',
  UPDATE_AWA_PREFERENCE: '/api/hrxPluginService/update-awa-preference',
  GET_AWA_PREFERENCE: '/api/hrxPluginService/get-awa-preferences',
  GET_A_TO_Z_GLOBAL_NAV: '/api/hrxPluginService/get-atoz-global-navbar',
  EXECUTE_SPT_PROCESSING: '/api/hrxPluginService/execute-spt-processing',
  GET_AGENTS_CAPACITY: '/api/hrxPluginService/get-agents-capacity',
  UPDATE_AGENT_CAPACITY: '/api/hrxPluginService/batchUpdateAgentCapacity',
  GET_EXACT_WORKFLOW_FORM_DETAILS: '/api/hrxPluginService/get-exact-investigation-workflow-form-details',
  UPDATE_EXACT_WORKFLOW_FORM_DETAILS: '/api/hrxPluginService/update-exact-investigation-workflow-form-details',
  UPDATE_EXACT_WORKFLOW: '/api/hrxPluginService/update-exact-investigation-workflow',
  GET_EXACT_WORKFLOW_APPROVAL_HISTORY: '/api/hrxPluginService/get-exact-workflow-approval-history',
  GET_EXACT_TERMINATION_APPROVAL_LIST: '/api/hrxPluginService/get-exact-termination-approval-list',
  UPDATE_EXTERNAL_CONTACT: '/api/hrxPluginService/update-external-contact',
  SEND_CASE_ACCESS_REQUEST: '/api/hrxPluginService/send-case-access-request',
  GET_INVOLVED_PARTY_SUMMARY: '/api/hrxPluginService/get-involved-party-summary',

  // Access Management Service Endpoints
  CHECK_USER_PERMISSIONS: '/api/hrxAccessManagementService/check-user-permissions',
  GET_PERMISSIONS_SCOPE_BY_USER: '/api/hrxAccessManagementService/get-permissions-scope-by-user',
  GET_USER_PERMISSIONS: '/api/hrxAccessManagementService/get-user-permissions',
  IDENTIFY_HR_ROLE: '/api/hrxAccessManagementService/identify-hr-role',
  GET_ROLE_MEMBERSHIP: '/api/hrxAccessManagementService/get-role-membership',
  SEARCH_ROLE_MEMBERSHIPS: '/api/hrxAccessManagementService/search-role-memberships',
  CREATE_MEMBER_SCOPE_ADHOC: '/api/hrxAccessManagementService/create-member-scope-adhoc',
  GET_UPLOAD_ATTACHMENT_URL: '/api/hrxPluginService/get-upload-attachment-url',
  CREATE_ATTACHMENT_URL: '/api/hrxPluginService/create-attachment',
  GET_DOWNLOAD_ATTACHMENT_URL: '/api/hrxPluginService/get-download-attachment-url',
  GET_ACT_ATTACHMENTS: '/api/hrxPluginService/get-act-attachments',
  GET_ACT_DOWNLOAD_ATTACHMENT_URL: '/api/hrxPluginService/get-act-download-attachment-url',
  GET_ATTACHMENTS: '/api/hrxPluginService/get-attachments',
  EXACT_PROXY_CALL: '/api/hrxPluginService/exact-proxy-call',
  SEND_TRANSCRIPT_CONSENT: '/api/hrxPluginService/send-transcript-consent',

  // Notification Service Endpoints
  GET_EMPLOYEE_NOTIFICATIONS: '/api/hrxNotificationDeliveryService/employee/{employeeId}/notifications',
  UPDATE_EMPLOYEE_NOTIFICATION:
    '/api/hrxNotificationDeliveryService/employee/{employeeId}/notifications/{notificationId}',
  CREATE_NOTIFICATION: '/api/hrxNotificationDeliveryService/notifications',

  // AtoZ Reverse Proxy Endpoints
  ATOZ_SEARCH_EMPLOYEE: '/apis/AtoZHRxPluginService/search-employee',
  ATOZ_GET_CASES: '/apis/AtoZHRxPluginService/get-cases',
};

export interface ExternalURLS {
  REINSTATEMENT_FORM: string;
  NA_OPS_PXT_STANDARD_WORK_FIELD_GUIDES: string;
  REHIRE_ELIGIBILITY_ENGINE: string;
  EMPLOYEE_DOCUMENTS_PORTAL: string;
  LiveSupportLOA: string;
  LiveSupportHRSupport: string;
  LiveSupportPanoCreateCase: string;
  ManageMyLeave: string;
  PEOPLE_PORTAL: string;
  PHONE_TOOL: string;
  ADMIN_DIRECTORY: string;
  HR_KEY: string;
  ALLSEC: string;
  SIM_T: string;
  SMART_FORMS: string;
  QUICK_SIGHT: string;
  CLARITY: string;
  PANORAMA: string;
  ACCESS_DENIED_PAGE_IMAGE: string;
  TCUI_PORTAL?: string; //TODO prod link
  INTERNAL_SERVER_ERROR_IMAGE: string;
  AMAZON_LOGO: string;
  HRSC_LOGO: string;
  HELP_CREATE_ISSUE: string;
  SUBMIT_FEEDBACK: string;
  CAMP: string;
  LENEL: string;
  FCLM: string;
  WIKI: string;
  HR_TOOLKIT: string;
  EMPLOYEE_POLICIES: string;
  DLS: string;
  IVY: string;
  ADAPT: string;
  PAGE_NOT_FOUND_IMAGE: string;
  SUCCESS_IMAGE: string;
  CUBE_NEWS: string;
  KNOWLEDGE_BASE: string;
  EXACT: string;
  UMBRELLA: string;
  ONBASE: string;
  WELCOME_BANNER_IMAGE: string;
  BEXT_DASHBOARD: string;
  SUPPORT: string;
  GSS: string;
  ATOZ_MY_HR: string;
  MAPLE_MY_SUPPORT_ASSIGNMENTS: string;
  DALI_LINK_PREFIX: string;
  PXT_ONE_QUICKSIGHT_DASHBOARD: string;
  PXT_ONE_NO_ACCESS_IMAGE: string;
  ENGAGE: string;
  CONNECTIONS: string;
  POLICY_2_0: string;
  LABOR_SCHEDULING_TOOL: string;
  HRSC_CASE_METRICS: string;
  HAWKEYE_HMD: string;
  RISE: string;
  MAPLE_SELF_SERVICE_USER_PROFILE: string;
  CREATE_ACCESS_ISSUE_TICKET: string;
  POLICY_BY_LOCATION: string;
  POLICY_STYLE_GUIDE: string;
  POLICY_TEAM_WIKI: string;
  REQUEST_NEW_POLICY: string;
  REQUEST_POLICY_CHANGE: string;
  ORIGAMI: string;
  PXT_ONE_FAQ_WIKI: string;
  CLEAR_COOKIES_GUIDE: string;
  NA_OPS_PXT_RESOURCE_HUB: string;
  PXT_SUPPORT_RESOURCES: string;
  HR_SUPPORT_TOOLKIT: string;
  VOA_EXPLORER: string;
  AMAZON_HIRE: string;
  JOB_FINDER: string;
  ICIMS_RECRUITING_REQUISITION_MANAGMENT: string;
  ERCI_INTAKE: string;
  DEI_RESOURCES: string;
  WWAS_TALENT_MANAGEMENT: string;
  PXT_ACADEMY: string;
  WORKFORCE_DATA_MANAGEMENT_TOOL: string;
  REQUEST_PXT_ALERT: string;
  REQUEST_COMMS_SUPPORT: string;
  COMMS_SELF_SERVICE: string;
  COMMS_TEAM_WIKI: string;
  CONTACT_PXT_COMMS: string;
  CM_FRAMEWORK_AND_ESTTENSITALS_RESOURCES: string;
  REQUEST_CM_SUPPORT: string;
  CHANGE_MANAGMENT_WIKI: string;
  CONTACT_CHANGE_MANAGEMENT: string;
  QUICKSIGHT_EMPLOYEE_DATA_SUMMARY: string;
  QUICKSIGHT_SINGLE_EMPLOYEE: string;
  SAY_HI: string;
  LINGUISTIC_ACCESSIBILITY_WIKI: string;
}

export type ExternalURLKey = keyof ExternalURLS;

/**
 * Constants all External URLs by stage
 */
export const EXTERNAL_URLS: Record<STAGE, ExternalURLS> = {
  [STAGE.DEV]: {
    REINSTATEMENT_FORM: 'https://console.harmony.a2z.com/exits-ci/ReinstatementIntake',
    NA_OPS_PXT_STANDARD_WORK_FIELD_GUIDES: 'https://atoz.amazon.work/pages/na-ops-pxt/Standard-Work-Field-Guides',
    REHIRE_ELIGIBILITY_ENGINE: 'https://ree.chip.people-tech.a2z.com/',
    EMPLOYEE_DOCUMENTS_PORTAL: 'https://docs.amazon.com/admin/us/dashboard/documents',
    LiveSupportLOA:
      'https://essportal.integ.amazon.com/myhr/livehelp/landing?category=HR+Support+on+behalf+of+an+employee&type=Chat+with+Leave+and+Accommodations',
    LiveSupportHRSupport:
      'https://essportal.integ.amazon.com/myhr/livehelp/landing?category=HR+Support+on+behalf+of+an+employee&type=Chat+with+HR+Support',
    LiveSupportPanoCreateCase: 'https://essportal.integ.amazon.com/myhr/livehelp/landing?category=Test&type=HRSC',
    ManageMyLeave: 'https://essportal.integ.amazon.com/myhr/category/time,_leave_of_absence_and_accommodation',
    PEOPLE_PORTAL: 'https://peopleportal.hr.corp.amazon.com/',
    PHONE_TOOL: 'https://phonetool.amazon.com/users/',
    HR_KEY: 'https://hrkey.amazon.com/',
    ALLSEC: 'https://www.allsechro.com/AmazonSSO/',
    SIM_T: 'https://t.corp.amazon.com/',
    SMART_FORMS: 'https://smarthr-beta.integ.amazon.com',
    QUICK_SIGHT: 'https://wisdom.corp.amazon.com/quicksight',
    CLARITY: 'https://clarity.people-insight.a2z.com/',
    PANORAMA: 'https://amazon-hr.my.salesforce.com/',
    BEXT_DASHBOARD:
      'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/e7c88c97-a921-4e74-9d24-d4884cc06f10/sheets/e7c88c97-a921-4e74-9d24-d4884cc06f10_7f1c1d1c-1375-4b10-90ee-d4fcab29f7b5',
    TCUI_PORTAL: 'https://weui-test.workevents.a2z.com/teams-timecard/',
    INTERNAL_SERVER_ERROR_IMAGE: 'https://alpha.hrsc.pxt.amazon.dev/assets/FAILURE_ERROR_IMAGE.png',
    AMAZON_LOGO: 'https://alpha.hrsc.pxt.amazon.dev/assets/AMAZON_LOGO.png', //TODO Remove Amazon LOGO
    HRSC_LOGO: 'https://alpha.hrsc.pxt.amazon.dev/assets/HRSC_Logo_RV_V2.png',
    HELP_CREATE_ISSUE: 'https://sim.amazon.com/issues/create?template=50c2f318-6858-4623-ab68-8e0c13e0f18a',
    SUBMIT_FEEDBACK: 'https://survey.fieldsense.whs.amazon.dev/survey/252cfef4-b4ae-46bf-99c4-6450708c0b60',
    CAMP: 'https://camp-beta.corp.amazon.com/',
    LENEL: 'http://badgescanreport-beta.corp.amazon.com/',
    FCLM: 'https://fclm-portal.integ.amazon.com/',
    WIKI: 'https://w.amazon.com/bin/view/Main/',
    HR_TOOLKIT: 'https://inside.hr.amazon.dev/content/inside/us/en/toolkits/for-hr.html',
    EMPLOYEE_POLICIES: 'https://inside.hr.amazon.dev/us/en/employment/policies-global.html',
    DLS: 'https://landdqa-amazonessportal.cs45.force.com/ESSPortal/s/',
    IVY: 'https://beta.ivy.talent.a2z.com/',
    ADAPT: 'https://adapt-gamma.amazon.com',
    ACCESS_DENIED_PAGE_IMAGE: 'https://alpha.hrsc.pxt.amazon.dev/assets/output.png',
    PAGE_NOT_FOUND_IMAGE: 'https://alpha.hrsc.pxt.amazon.dev/assets/404.png',
    SUCCESS_IMAGE: 'https://alpha.hrsc.pxt.amazon.dev/assets/success.png',
    ADMIN_DIRECTORY: 'https://admindirectory.corp.amazon.com/users/',
    CUBE_NEWS: 'https://news.cube.pxt.amazon.dev/',
    KNOWLEDGE_BASE: 'https://dev.hrsc-kb.pxt.amazon.dev/etc/amazon-hr/paths/search-proxy.html',
    EXACT: 'https://exactinvestigation.my.salesforce.com/',
    UMBRELLA: 'https://iad.umbrella.amazon.dev/sdl/dashboard',
    ONBASE: 'https://amazon.onbaseonline.com/idpappnet/',
    WELCOME_BANNER_IMAGE: 'https://alpha.hrsc.pxt.amazon.dev/assets/WELCOME_BANNER_IMAGE.png',
    SUPPORT: 'https://alpha.hrsc.pxt.amazon.dev/assets/SUPPORT.png',
    GSS: 'https://na.labor-admin.gsf.a2z.com/scheduling',
    ATOZ_MY_HR: 'https://essportal.integ.amazon.com/myhr',
    MAPLE_MY_SUPPORT_ASSIGNMENTS: 'https://maple-admin-beta.integ.amazon.com/my-support-assignments/populations',
    DALI_LINK_PREFIX: 'https://amazonloaclaimvantage--ldpreprod.sandbox.lightning.force.com/lightning/r/',
    PXT_ONE_QUICKSIGHT_DASHBOARD:
      'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/80e8a75b-555e-4c17-9f1a-ff8f5962c4f8',
    PXT_ONE_NO_ACCESS_IMAGE: 'https://alpha.hrsc.pxt.amazon.dev/assets/lock.svg',
    ENGAGE: 'https://na.beta.engage.amazon.dev/',
    CONNECTIONS:
      'https://connections-reporting.corp.amazon.com/#/home?currentView=ORGS&selectedTimeRangeId=static-1-month',
    POLICY_2_0: 'https://policy.a2z.com/',
    LABOR_SCHEDULING_TOOL: 'https://scheduling.amazon.com/',
    HRSC_CASE_METRICS:
      'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/e15aa502-36cc-43aa-ac62-9cc2ec0472c6/sheets/e15aa502-36cc-43aa-ac62-9cc2ec0472c6_6e1dfb97-e3ed-4ae5-8795-0720df85bb20',
    HAWKEYE_HMD:
      'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/efbda839-26fc-45bc-85e0-79b6af874aa3/sheets/efbda839-26fc-45bc-85e0-79b6af874aa3_fd324358-8075-4c39-89c0-6982f35cae14',
    RISE: 'https://rise.aka.amazon.com/overview',
    MAPLE_SELF_SERVICE_USER_PROFILE:
      'https://w.amazon.com/bin/view/FPDS/FPDS-DCX/DCX-Security/MapleMySupport/UserGuide/UserProfile/',
    CREATE_ACCESS_ISSUE_TICKET: 'https://t.corp.amazon.com/create/templates/a504176f-7a82-4add-821c-75819b387593',
    POLICY_BY_LOCATION: 'https://inside.hr.amazon.dev/us/en/employment/policies-global.html',
    POLICY_STYLE_GUIDE: 'https://w.amazon.com/bin/view/HR_Global_Content_Team/Style_Guide',
    POLICY_TEAM_WIKI: 'https://w.amazon.com/bin/view/PXT3-policy',
    REQUEST_NEW_POLICY: 'https://issues.amazon.com/issues/create?template=199b0334-87bd-4e05-8d6b-755e49d1cfc0',
    REQUEST_POLICY_CHANGE: 'https://form.asana.com/?k=ZuEAhmZPM8P2ymblBb9-kQ&d=8442528107068',
    ORIGAMI: 'https://live.origamirisk.com/Origami/IncidentEntry/Welcome',
    PXT_ONE_FAQ_WIKI: 'https://w.amazon.com/bin/view/PXT_Apps/HRSC/Features/PXT_One_Request/FAQs/',
    CLEAR_COOKIES_GUIDE: 'https://w.amazon.com/bin/view/HRIS/How-to-clear-browser-cache-and-cookies',
    NA_OPS_PXT_RESOURCE_HUB: 'https://w.amazon.com/bin/view/NA_Ops_PXT/Resource_Hub/',
    PXT_SUPPORT_RESOURCES: 'https://w.amazon.com/bin/view/PXT_Support',
    HR_SUPPORT_TOOLKIT: 'https://inside.hr.amazon.dev/us/en/toolkits/for-hr/toolkits/hr-support.html',
    VOA_EXPLORER: 'https://peopleinsightvoaexplorer.corp.amazon.com/explorer',
    AMAZON_HIRE: 'https://hire.amazon.com/',
    JOB_FINDER: 'https://jobfinder.amazon.com/',
    ICIMS_RECRUITING_REQUISITION_MANAGMENT:
      'https://amazon.icims.com/icims2/servlet/icims2?module=Root&action=login&sso=1&hashed=-1353432425',
    ERCI_INTAKE: 'https://w.amazon.com/bin/view/ERCI_WIKI/Home/ERCI_Intake_Process/',
    DEI_RESOURCES: 'https://w.amazon.com/bin/view/Gdeiofferingsupport',
    WWAS_TALENT_MANAGEMENT: 'https://w.amazon.com/bin/view/ConsumerTalentManagement/',
    PXT_ACADEMY: 'https://w.amazon.com/bin/view/GlobalOpsPXTAcademy/',
    WORKFORCE_DATA_MANAGEMENT_TOOL: 'https://wdmt.pxt.amazon.dev/',
    REQUEST_PXT_ALERT: 'https://issues.amazon.com/issues/create?template=9f007690-4d9c-42c5-9fb7-5f52bb3e29d8',
    REQUEST_COMMS_SUPPORT: 'https://issues.amazon.com/issues/create?template=3767f065-0c3b-4436-88bb-cf36a0d7dd4a',
    COMMS_SELF_SERVICE: 'https://w.amazon.com/bin/view/PXT3-comms/intake-and-support/self-service/',
    COMMS_TEAM_WIKI: 'https://w.amazon.com/bin/view/PXT3-comms/',
    CONTACT_PXT_COMMS: 'mailto:pxtcomms@amazon.com',
    CM_FRAMEWORK_AND_ESTTENSITALS_RESOURCES:
      'https://inside.amazon.com/en/toolkits/ChangeManagement/Pages/CM_Framework_Essentials.aspx',
    REQUEST_CM_SUPPORT: 'https://w.amazon.com/bin/view/PXT3-CM/CM_Resources/CM_Intake/',
    CHANGE_MANAGMENT_WIKI: 'https://w.amazon.com/bin/view/PXT3-CM/',
    CONTACT_CHANGE_MANAGEMENT: 'mailto:PXT3-Change@amazon.com',
    QUICKSIGHT_EMPLOYEE_DATA_SUMMARY:
      'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/2014b362-7b80-4cb9-9daa-c341e164ca79/sheets/2014b362-7b80-4cb9-9daa-c341e164ca79_6ea7dfdf-3f36-4933-94b1-c197855e0df3',
    QUICKSIGHT_SINGLE_EMPLOYEE:
      'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/d7cf06d9-65cb-4c7a-988a-72ae3bdbd78e/sheets/d7cf06d9-65cb-4c7a-988a-72ae3bdbd78e_50070724-25b8-4353-aeb9-3d55f4735955?#',
    SAY_HI: 'https://sayhi.pxt.amazon.dev/',
    LINGUISTIC_ACCESSIBILITY_WIKI: 'https://w.amazon.com/bin/view/LinguisticAccessibilityToolkit/MLCResources/',
  },
  [STAGE.ALPHA]: {
    REINSTATEMENT_FORM: 'https://console.harmony.a2z.com/exits-ci/ReinstatementIntake',
    NA_OPS_PXT_STANDARD_WORK_FIELD_GUIDES: 'https://atoz.amazon.work/pages/na-ops-pxt/Standard-Work-Field-Guides',
    REHIRE_ELIGIBILITY_ENGINE: 'https://ree.chip.people-tech.a2z.com/',
    EMPLOYEE_DOCUMENTS_PORTAL: 'https://docs.amazon.com/admin/us/dashboard/documents',
    LiveSupportLOA:
      'https://essportal.integ.amazon.com/myhr/livehelp/landing?category=HR+Support+on+behalf+of+an+employee&type=Chat+with+Leave+and+Accommodations',
    LiveSupportHRSupport:
      'https://essportal.integ.amazon.com/myhr/livehelp/landing?category=HR+Support+on+behalf+of+an+employee&type=Chat+with+HR+Support',
    LiveSupportPanoCreateCase: 'https://essportal.integ.amazon.com/myhr/livehelp/landing?category=Test&type=HRSC',
    ManageMyLeave: 'https://essportal.integ.amazon.com/myhr/category/time,_leave_of_absence_and_accommodation',
    PEOPLE_PORTAL: 'https://peopleportal.hr.corp.amazon.com/',
    PHONE_TOOL: 'https://phonetool.amazon.com/users/',
    HR_KEY: 'https://hrkey.amazon.com/',
    ALLSEC: 'https://www.allsechro.com/AmazonSSO/',
    SIM_T: 'https://t.corp.amazon.com/',
    SMART_FORMS: 'https://smarthr-beta.integ.amazon.com',
    QUICK_SIGHT: 'https://wisdom.corp.amazon.com/quicksight',
    CLARITY: 'https://clarity.people-insight.a2z.com/',
    PANORAMA: 'https://amazon-hr.my.salesforce.com/',
    BEXT_DASHBOARD:
      'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/e7c88c97-a921-4e74-9d24-d4884cc06f10/sheets/e7c88c97-a921-4e74-9d24-d4884cc06f10_7f1c1d1c-1375-4b10-90ee-d4fcab29f7b5',
    TCUI_PORTAL: 'https://weui-test.workevents.a2z.com/teams-timecard/',
    INTERNAL_SERVER_ERROR_IMAGE: 'https://alpha.hrsc.pxt.amazon.dev/assets/FAILURE_ERROR_IMAGE.png',
    AMAZON_LOGO: 'https://alpha.hrsc.pxt.amazon.dev/assets/AMAZON_LOGO.png', //TODO Remove Amazon LOGO
    HRSC_LOGO: 'https://alpha.hrsc.pxt.amazon.dev/assets/HRSC_Logo_RV_V2.png',
    HELP_CREATE_ISSUE: 'https://sim.amazon.com/issues/create?template=50c2f318-6858-4623-ab68-8e0c13e0f18a',
    SUBMIT_FEEDBACK: 'https://survey.fieldsense.whs.amazon.dev/survey/252cfef4-b4ae-46bf-99c4-6450708c0b60',
    CAMP: 'https://camp-beta.corp.amazon.com/',
    LENEL: 'http://badgescanreport-beta.corp.amazon.com/',
    FCLM: 'https://fclm-portal.integ.amazon.com/',
    WIKI: 'https://w.amazon.com/bin/view/Main/',
    HR_TOOLKIT: 'https://inside.hr.amazon.dev/content/inside/us/en/toolkits/for-hr.html',
    EMPLOYEE_POLICIES: 'https://inside.hr.amazon.dev/us/en/employment/policies-global.html',
    DLS: 'https://landdqa-amazonessportal.cs45.force.com/ESSPortal/s/',
    IVY: 'https://beta.ivy.talent.a2z.com/',
    ADAPT: 'https://adapt-gamma.amazon.com',
    ACCESS_DENIED_PAGE_IMAGE: 'https://alpha.hrsc.pxt.amazon.dev/assets/output.png',
    PAGE_NOT_FOUND_IMAGE: 'https://alpha.hrsc.pxt.amazon.dev/assets/404.png',
    SUCCESS_IMAGE: 'https://alpha.hrsc.pxt.amazon.dev/assets/success.png',
    ADMIN_DIRECTORY: 'https://admindirectory.corp.amazon.com/users/',
    CUBE_NEWS: 'https://news.cube.pxt.amazon.dev/',
    KNOWLEDGE_BASE: 'https://dev.hrsc-kb.pxt.amazon.dev/etc/amazon-hr/paths/search-proxy.html',
    EXACT: 'https://exactinvestigation.my.salesforce.com/',
    UMBRELLA: 'https://iad.umbrella.amazon.dev/sdl/dashboard',
    ONBASE: 'https://amazon.onbaseonline.com/idpappnet/',
    WELCOME_BANNER_IMAGE: 'https://alpha.hrsc.pxt.amazon.dev/assets/WELCOME_BANNER_IMAGE.png',
    SUPPORT: 'https://alpha.hrsc.pxt.amazon.dev/assets/SUPPORT.png',
    GSS: 'https://na.labor-admin.gsf.a2z.com/scheduling',
    ATOZ_MY_HR: 'https://essportal.integ.amazon.com/myhr',
    MAPLE_MY_SUPPORT_ASSIGNMENTS: 'https://maple-admin-beta.integ.amazon.com/my-support-assignments/populations',
    DALI_LINK_PREFIX: 'https://amazonloaclaimvantage--ldpreprod.sandbox.lightning.force.com/lightning/r/',
    PXT_ONE_QUICKSIGHT_DASHBOARD:
      'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/80e8a75b-555e-4c17-9f1a-ff8f5962c4f8',
    PXT_ONE_NO_ACCESS_IMAGE: 'https://alpha.hrsc.pxt.amazon.dev/assets/lock.svg',
    ENGAGE: 'https://na.beta.engage.amazon.dev/',
    CONNECTIONS:
      'https://connections-reporting.corp.amazon.com/#/home?currentView=ORGS&selectedTimeRangeId=static-1-month',
    POLICY_2_0: 'https://policy.a2z.com/',
    LABOR_SCHEDULING_TOOL: 'https://scheduling.amazon.com/',
    HRSC_CASE_METRICS:
      'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/e15aa502-36cc-43aa-ac62-9cc2ec0472c6/sheets/e15aa502-36cc-43aa-ac62-9cc2ec0472c6_6e1dfb97-e3ed-4ae5-8795-0720df85bb20',
    HAWKEYE_HMD:
      'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/efbda839-26fc-45bc-85e0-79b6af874aa3/sheets/efbda839-26fc-45bc-85e0-79b6af874aa3_fd324358-8075-4c39-89c0-6982f35cae14',
    RISE: 'https://rise.aka.amazon.com/overview',
    MAPLE_SELF_SERVICE_USER_PROFILE:
      'https://w.amazon.com/bin/view/FPDS/FPDS-DCX/DCX-Security/MapleMySupport/UserGuide/UserProfile/',
    CREATE_ACCESS_ISSUE_TICKET: 'https://t.corp.amazon.com/create/templates/a504176f-7a82-4add-821c-75819b387593',
    POLICY_BY_LOCATION: 'https://inside.hr.amazon.dev/us/en/employment/policies-global.html',
    POLICY_STYLE_GUIDE: 'https://w.amazon.com/bin/view/HR_Global_Content_Team/Style_Guide',
    POLICY_TEAM_WIKI: 'https://w.amazon.com/bin/view/PXT3-policy',
    REQUEST_NEW_POLICY: 'https://issues.amazon.com/issues/create?template=199b0334-87bd-4e05-8d6b-755e49d1cfc0',
    REQUEST_POLICY_CHANGE: 'https://form.asana.com/?k=ZuEAhmZPM8P2ymblBb9-kQ&d=8442528107068',
    ORIGAMI: 'https://live.origamirisk.com/Origami/IncidentEntry/Welcome',
    PXT_ONE_FAQ_WIKI: 'https://w.amazon.com/bin/view/PXT_Apps/HRSC/Features/PXT_One_Request/FAQs/',
    CLEAR_COOKIES_GUIDE: 'https://w.amazon.com/bin/view/HRIS/How-to-clear-browser-cache-and-cookies',
    NA_OPS_PXT_RESOURCE_HUB: 'https://w.amazon.com/bin/view/NA_Ops_PXT/Resource_Hub/',
    PXT_SUPPORT_RESOURCES: 'https://w.amazon.com/bin/view/PXT_Support',
    HR_SUPPORT_TOOLKIT: 'https://inside.hr.amazon.dev/us/en/toolkits/for-hr/toolkits/hr-support.html',
    VOA_EXPLORER: 'https://peopleinsightvoaexplorer.corp.amazon.com/explorer',
    AMAZON_HIRE: 'https://hire.amazon.com/',
    JOB_FINDER: 'https://jobfinder.amazon.com/',
    ICIMS_RECRUITING_REQUISITION_MANAGMENT:
      'https://amazon.icims.com/icims2/servlet/icims2?module=Root&action=login&sso=1&hashed=-1353432425',
    ERCI_INTAKE: 'https://w.amazon.com/bin/view/ERCI_WIKI/Home/ERCI_Intake_Process/',
    DEI_RESOURCES: 'https://w.amazon.com/bin/view/Gdeiofferingsupport',
    WWAS_TALENT_MANAGEMENT: 'https://w.amazon.com/bin/view/ConsumerTalentManagement/',
    PXT_ACADEMY: 'https://w.amazon.com/bin/view/GlobalOpsPXTAcademy/',
    WORKFORCE_DATA_MANAGEMENT_TOOL: 'https://wdmt.pxt.amazon.dev/',
    REQUEST_PXT_ALERT: 'https://issues.amazon.com/issues/create?template=9f007690-4d9c-42c5-9fb7-5f52bb3e29d8',
    REQUEST_COMMS_SUPPORT: 'https://issues.amazon.com/issues/create?template=3767f065-0c3b-4436-88bb-cf36a0d7dd4a',
    COMMS_SELF_SERVICE: 'https://w.amazon.com/bin/view/PXT3-comms/intake-and-support/self-service/',
    COMMS_TEAM_WIKI: 'https://w.amazon.com/bin/view/PXT3-comms/',
    CONTACT_PXT_COMMS: 'mailto:pxtcomms@amazon.com',
    CM_FRAMEWORK_AND_ESTTENSITALS_RESOURCES:
      'https://inside.amazon.com/en/toolkits/ChangeManagement/Pages/CM_Framework_Essentials.aspx',
    REQUEST_CM_SUPPORT: 'https://w.amazon.com/bin/view/PXT3-CM/CM_Resources/CM_Intake/',
    CHANGE_MANAGMENT_WIKI: 'https://w.amazon.com/bin/view/PXT3-CM/',
    CONTACT_CHANGE_MANAGEMENT: 'mailto:PXT3-Change@amazon.com',
    QUICKSIGHT_EMPLOYEE_DATA_SUMMARY:
      'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/2014b362-7b80-4cb9-9daa-c341e164ca79/sheets/2014b362-7b80-4cb9-9daa-c341e164ca79_6ea7dfdf-3f36-4933-94b1-c197855e0df3',
    QUICKSIGHT_SINGLE_EMPLOYEE:
      'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/d7cf06d9-65cb-4c7a-988a-72ae3bdbd78e/sheets/d7cf06d9-65cb-4c7a-988a-72ae3bdbd78e_50070724-25b8-4353-aeb9-3d55f4735955?#',
    SAY_HI: 'https://sayhi.pxt.amazon.dev/',
    LINGUISTIC_ACCESSIBILITY_WIKI: 'https://w.amazon.com/bin/view/LinguisticAccessibilityToolkit/MLCResources/',
  },
  [STAGE.BETA]: {
    REINSTATEMENT_FORM: 'https://console.harmony.a2z.com/exits-ci/ReinstatementIntake',
    NA_OPS_PXT_STANDARD_WORK_FIELD_GUIDES: 'https://atoz.amazon.work/pages/na-ops-pxt/Standard-Work-Field-Guides',
    REHIRE_ELIGIBILITY_ENGINE: 'https://ree.chip.people-tech.a2z.com/',
    EMPLOYEE_DOCUMENTS_PORTAL: 'https://docs.amazon.com/admin/us/dashboard/documents',
    LiveSupportLOA:
      'https://essportal.integ.amazon.com/myhr/livehelp/landing?category=HR+Support+on+behalf+of+an+employee&type=Chat+with+Leave+and+Accommodations',
    LiveSupportHRSupport:
      'https://essportal.integ.amazon.com/myhr/livehelp/landing?category=HR+Support+on+behalf+of+an+employee&type=Chat+with+HR+Support',
    LiveSupportPanoCreateCase: 'https://essportal.integ.amazon.com/myhr/livehelp/landing?category=Test&type=HRSC',
    ManageMyLeave: 'https://essportal.integ.amazon.com/myhr/category/time,_leave_of_absence_and_accommodation',
    PEOPLE_PORTAL: 'https://peopleportal.hr.corp.amazon.com/',
    PHONE_TOOL: 'https://phonetool.amazon.com/users/',
    HR_KEY: 'https://hrkey.amazon.com/',
    ALLSEC: 'https://www.allsechro.com/AmazonSSO/',
    SIM_T: 'https://t.corp.amazon.com/',
    SMART_FORMS: 'https://smarthr-beta.integ.amazon.com',
    QUICK_SIGHT: 'https://wisdom.corp.amazon.com/quicksight',
    CLARITY: 'https://clarity.people-insight.a2z.com/',
    PANORAMA: 'https://amazon-hr.my.salesforce.com/',
    BEXT_DASHBOARD:
      'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/e7c88c97-a921-4e74-9d24-d4884cc06f10/sheets/e7c88c97-a921-4e74-9d24-d4884cc06f10_7f1c1d1c-1375-4b10-90ee-d4fcab29f7b5',
    TCUI_PORTAL: 'https://weui-test.workevents.a2z.com/teams-timecard/',
    ACCESS_DENIED_PAGE_IMAGE: 'https://beta.hrsc.pxt.amazon.dev/assets/UNAUTHORIZED.png',
    INTERNAL_SERVER_ERROR_IMAGE: 'https://beta.hrsc.pxt.amazon.dev/assets/FAILURE_ERROR_IMAGE.png',
    AMAZON_LOGO: 'https://beta.hrsc.pxt.amazon.dev/assets/AMAZON_LOGO.png', //TODO Remove Amazon LOGO
    HRSC_LOGO: 'https://beta.hrsc.pxt.amazon.dev/assets/HRSC_Logo_RV_V2.png',
    HELP_CREATE_ISSUE: 'https://sim.amazon.com/issues/create?template=50c2f318-6858-4623-ab68-8e0c13e0f18a',
    SUBMIT_FEEDBACK: 'https://survey.fieldsense.whs.amazon.dev/survey/252cfef4-b4ae-46bf-99c4-6450708c0b60',
    CAMP: 'https://camp-beta.corp.amazon.com/',
    LENEL: 'http://badgescanreport-beta.corp.amazon.com/',
    FCLM: 'https://fclm-portal.integ.amazon.com/',
    WIKI: 'https://w.amazon.com/bin/view/Main/',
    HR_TOOLKIT: 'https://inside.hr.amazon.dev/content/inside/us/en/toolkits/for-hr.html',
    EMPLOYEE_POLICIES: 'https://inside.hr.amazon.dev/us/en/employment/policies-global.html',
    DLS: 'https://landdqa-amazonessportal.cs45.force.com/ESSPortal/s/',
    IVY: 'https://beta.ivy.talent.a2z.com/',
    ADAPT: 'https://adapt-gamma.amazon.com',
    PAGE_NOT_FOUND_IMAGE: 'https://beta.hrsc.pxt.amazon.dev/assets/404.png',
    SUCCESS_IMAGE: 'https://beta.hrsc.pxt.amazon.dev/assets/success.png',
    ADMIN_DIRECTORY: 'https://admindirectory.corp.amazon.com/users/',
    CUBE_NEWS: 'https://news.cube.pxt.amazon.dev/',
    KNOWLEDGE_BASE: 'https://dev.hrsc-kb.pxt.amazon.dev/etc/amazon-hr/paths/search-proxy.html',
    EXACT: 'https://exactinvestigation.my.salesforce.com/',
    UMBRELLA: 'https://iad.umbrella.amazon.dev/sdl/dashboard',
    ONBASE: 'https://amazon.onbaseonline.com/idpappnet/',
    WELCOME_BANNER_IMAGE: 'https://beta.hrsc.pxt.amazon.dev/assets/WELCOME_BANNER_IMAGE.png',
    SUPPORT: 'https://beta.hrsc.pxt.amazon.dev/assets/SUPPORT.png',
    GSS: 'https://na.labor-admin.gsf.a2z.com/scheduling',
    ATOZ_MY_HR: 'https://essportal.integ.amazon.com/myhr',
    MAPLE_MY_SUPPORT_ASSIGNMENTS: 'https://maple-admin-beta.integ.amazon.com/my-support-assignments/populations',
    DALI_LINK_PREFIX: 'https://amazonloaclaimvantage--ldpreprod.sandbox.lightning.force.com/lightning/r/',
    PXT_ONE_QUICKSIGHT_DASHBOARD:
      'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/80e8a75b-555e-4c17-9f1a-ff8f5962c4f8',
    PXT_ONE_NO_ACCESS_IMAGE: 'https://beta.hrsc.pxt.amazon.dev/assets/lock.svg',
    ENGAGE: 'https://na.beta.engage.amazon.dev/',
    CONNECTIONS:
      'https://connections-reporting.corp.amazon.com/#/home?currentView=ORGS&selectedTimeRangeId=static-1-month',
    POLICY_2_0: 'https://policy.a2z.com/',
    LABOR_SCHEDULING_TOOL: 'https://scheduling.amazon.com/',
    HRSC_CASE_METRICS:
      'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/e15aa502-36cc-43aa-ac62-9cc2ec0472c6/sheets/e15aa502-36cc-43aa-ac62-9cc2ec0472c6_6e1dfb97-e3ed-4ae5-8795-0720df85bb20',
    HAWKEYE_HMD:
      'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/efbda839-26fc-45bc-85e0-79b6af874aa3/sheets/efbda839-26fc-45bc-85e0-79b6af874aa3_fd324358-8075-4c39-89c0-6982f35cae14',
    RISE: 'https://rise.aka.amazon.com/overview',
    MAPLE_SELF_SERVICE_USER_PROFILE:
      'https://w.amazon.com/bin/view/FPDS/FPDS-DCX/DCX-Security/MapleMySupport/UserGuide/UserProfile/',
    CREATE_ACCESS_ISSUE_TICKET: 'https://t.corp.amazon.com/create/templates/a504176f-7a82-4add-821c-75819b387593',
    POLICY_BY_LOCATION: 'https://inside.hr.amazon.dev/us/en/employment/policies-global.html',
    POLICY_STYLE_GUIDE: 'https://w.amazon.com/bin/view/HR_Global_Content_Team/Style_Guide',
    POLICY_TEAM_WIKI: 'https://w.amazon.com/bin/view/PXT3-policy',
    REQUEST_NEW_POLICY: 'https://issues.amazon.com/issues/create?template=199b0334-87bd-4e05-8d6b-755e49d1cfc0',
    REQUEST_POLICY_CHANGE: 'https://form.asana.com/?k=ZuEAhmZPM8P2ymblBb9-kQ&d=8442528107068',
    ORIGAMI: 'https://live.origamirisk.com/Origami/IncidentEntry/Welcome',
    PXT_ONE_FAQ_WIKI: 'https://w.amazon.com/bin/view/PXT_Apps/HRSC/Features/PXT_One_Request/FAQs/',
    CLEAR_COOKIES_GUIDE: 'https://w.amazon.com/bin/view/HRIS/How-to-clear-browser-cache-and-cookies',
    NA_OPS_PXT_RESOURCE_HUB: 'https://w.amazon.com/bin/view/NA_Ops_PXT/Resource_Hub/',
    PXT_SUPPORT_RESOURCES: 'https://w.amazon.com/bin/view/PXT_Support',
    HR_SUPPORT_TOOLKIT: 'https://inside.hr.amazon.dev/us/en/toolkits/for-hr/toolkits/hr-support.html',
    VOA_EXPLORER: 'https://peopleinsightvoaexplorer.corp.amazon.com/explorer',
    AMAZON_HIRE: 'https://hire.amazon.com/',
    JOB_FINDER: 'https://jobfinder.amazon.com/',
    ICIMS_RECRUITING_REQUISITION_MANAGMENT:
      'https://amazon.icims.com/icims2/servlet/icims2?module=Root&action=login&sso=1&hashed=-1353432425',
    ERCI_INTAKE: 'https://w.amazon.com/bin/view/ERCI_WIKI/Home/ERCI_Intake_Process/',
    DEI_RESOURCES: 'https://w.amazon.com/bin/view/Gdeiofferingsupport',
    WWAS_TALENT_MANAGEMENT: 'https://w.amazon.com/bin/view/ConsumerTalentManagement/',
    PXT_ACADEMY: 'https://w.amazon.com/bin/view/GlobalOpsPXTAcademy/',
    WORKFORCE_DATA_MANAGEMENT_TOOL: 'https://wdmt.pxt.amazon.dev/',
    REQUEST_PXT_ALERT: 'https://issues.amazon.com/issues/create?template=9f007690-4d9c-42c5-9fb7-5f52bb3e29d8',
    REQUEST_COMMS_SUPPORT: 'https://issues.amazon.com/issues/create?template=3767f065-0c3b-4436-88bb-cf36a0d7dd4a',
    COMMS_SELF_SERVICE: 'https://w.amazon.com/bin/view/PXT3-comms/intake-and-support/self-service/',
    COMMS_TEAM_WIKI: 'https://w.amazon.com/bin/view/PXT3-comms/',
    CONTACT_PXT_COMMS: 'mailto:pxtcomms@amazon.com',
    CM_FRAMEWORK_AND_ESTTENSITALS_RESOURCES:
      'https://inside.amazon.com/en/toolkits/ChangeManagement/Pages/CM_Framework_Essentials.aspx',
    REQUEST_CM_SUPPORT: 'https://w.amazon.com/bin/view/PXT3-CM/CM_Resources/CM_Intake/',
    CHANGE_MANAGMENT_WIKI: 'https://w.amazon.com/bin/view/PXT3-CM/',
    CONTACT_CHANGE_MANAGEMENT: 'mailto:PXT3-Change@amazon.com',
    QUICKSIGHT_EMPLOYEE_DATA_SUMMARY:
      'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/2014b362-7b80-4cb9-9daa-c341e164ca79/sheets/2014b362-7b80-4cb9-9daa-c341e164ca79_6ea7dfdf-3f36-4933-94b1-c197855e0df3',
    QUICKSIGHT_SINGLE_EMPLOYEE:
      'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/d7cf06d9-65cb-4c7a-988a-72ae3bdbd78e/sheets/d7cf06d9-65cb-4c7a-988a-72ae3bdbd78e_50070724-25b8-4353-aeb9-3d55f4735955?#',
    SAY_HI: 'https://sayhi.pxt.amazon.dev/',
    LINGUISTIC_ACCESSIBILITY_WIKI: 'https://w.amazon.com/bin/view/LinguisticAccessibilityToolkit/MLCResources/',
  },
  [STAGE.GAMMA]: {
    REINSTATEMENT_FORM: 'https://console.harmony.a2z.com/exits-ci/ReinstatementIntake',
    NA_OPS_PXT_STANDARD_WORK_FIELD_GUIDES: 'https://atoz.amazon.work/pages/na-ops-pxt/Standard-Work-Field-Guides',
    REHIRE_ELIGIBILITY_ENGINE: 'https://ree.chip.people-tech.a2z.com/',
    EMPLOYEE_DOCUMENTS_PORTAL: 'https://docs.amazon.com/admin/us/dashboard/documents',
    LiveSupportLOA:
      'https://essportal.integ.amazon.com/myhr/livehelp/landing?category=HR+Support+on+behalf+of+an+employee&type=Chat+with+Leave+and+Accommodations',
    LiveSupportHRSupport:
      'https://essportal.integ.amazon.com/myhr/livehelp/landing?category=HR+Support+on+behalf+of+an+employee&type=Chat+with+HR+Support',
    LiveSupportPanoCreateCase: 'https://atoz.amazon.work/myhr/livehelp/landing?category=Test&type=HRSC',
    ManageMyLeave: 'https://atoz.amazon.work/myhr/category/time,_leave_of_absence_and_accommodation',
    PEOPLE_PORTAL: 'https://peopleportal.hr.corp.amazon.com/',
    PHONE_TOOL: 'https://phonetool.amazon.com/users/',
    HR_KEY: 'https://hrkey.amazon.com/',
    ALLSEC: 'https://www.allsechro.com/AmazonSSO/',
    SIM_T: 'https://t.corp.amazon.com/',
    SMART_FORMS: 'https://smarthr-gamma.integ.amazon.com',
    QUICK_SIGHT: 'https://wisdom.corp.amazon.com/quicksight',
    CLARITY: 'https://clarity.people-insight.a2z.com/',
    PANORAMA: 'https://amazon-hr.my.salesforce.com/',
    BEXT_DASHBOARD:
      'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/e7c88c97-a921-4e74-9d24-d4884cc06f10/sheets/e7c88c97-a921-4e74-9d24-d4884cc06f10_7f1c1d1c-1375-4b10-90ee-d4fcab29f7b5',
    INTERNAL_SERVER_ERROR_IMAGE: 'https://gamma.hrsc.pxt.amazon.dev/assets/FAILURE_ERROR_IMAGE.png',
    HELP_CREATE_ISSUE: 'https://sim.amazon.com/issues/create?template=50c2f318-6858-4623-ab68-8e0c13e0f18a',
    AMAZON_LOGO: 'https://gamma.hrsc.pxt.amazon.dev/assets/AMAZON_LOGO.png', //TODO Remove Amazon LOGO
    HRSC_LOGO: 'https://gamma.hrsc.pxt.amazon.dev/assets/HRSC_Logo_RV_V2.png',
    SUBMIT_FEEDBACK: 'https://survey.fieldsense.whs.amazon.dev/survey/252cfef4-b4ae-46bf-99c4-6450708c0b60',
    CAMP: 'https://camp-gamma.corp.amazon.com/',
    LENEL: 'https://acs-badgescanreport.corp.amazon.com/',
    FCLM: 'https://fclm-portal.integ.amazon.com/',
    WIKI: 'https://w.amazon.com/bin/view/Main/',
    HR_TOOLKIT: 'https://inside.hr.amazon.dev/content/inside/us/en/toolkits/for-hr.html',
    EMPLOYEE_POLICIES: 'https://inside.hr.amazon.dev/us/en/employment/policies-global.html',
    DLS: 'https://landdqa-amazonessportal.cs45.force.com/ESSPortal/s/',
    IVY: 'https://gamma.ivy.talent.a2z.com/',
    ADAPT: 'https://adapt-gamma.amazon.com',
    TCUI_PORTAL: 'https://weui-gamma.workevents.a2z.com/teams-timecard/',
    ACCESS_DENIED_PAGE_IMAGE: 'https://gamma.hrsc.pxt.amazon.dev/assets/UNAUTHORIZED.png',
    PAGE_NOT_FOUND_IMAGE: 'https://gamma.hrsc.pxt.amazon.dev/assets/404.png',
    SUCCESS_IMAGE: 'https://gamma.hrsc.pxt.amazon.dev/assets/success.png',
    ADMIN_DIRECTORY: 'https://admindirectory.corp.amazon.com/users/',
    CUBE_NEWS: 'https://news.cube.pxt.amazon.dev/',
    KNOWLEDGE_BASE: 'https://stage.hrsc-kb.pxt.amazon.dev/etc/amazon-hr/paths/search-proxy.html',
    EXACT: 'https://exactinvestigation.my.salesforce.com/',
    UMBRELLA: 'https://iad.umbrella.amazon.dev/sdl/dashboard',
    ONBASE: 'https://amazon.onbaseonline.com/idpappnet/',
    WELCOME_BANNER_IMAGE: 'https://gamma.hrsc.pxt.amazon.dev/assets/WELCOME_BANNER_IMAGE.png',
    SUPPORT: 'https://gamma.hrsc.pxt.amazon.dev/assets/SUPPORT.png',
    GSS: 'https://na.labor-admin.gsf.a2z.com/scheduling',
    ATOZ_MY_HR: 'https://essportal.integ.amazon.com/myhr',
    MAPLE_MY_SUPPORT_ASSIGNMENTS: 'https://maple-admin-beta.integ.amazon.com/my-support-assignments/populations',
    DALI_LINK_PREFIX: 'https://amazonloaclaimvantage--ldpreprod.sandbox.lightning.force.com/lightning/r/',
    PXT_ONE_QUICKSIGHT_DASHBOARD:
      'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/80e8a75b-555e-4c17-9f1a-ff8f5962c4f8',
    PXT_ONE_NO_ACCESS_IMAGE: 'https://gamma.hrsc.pxt.amazon.dev/assets/lock.svg',
    ENGAGE: 'https://na.gamma.engage.amazon.dev/',
    CONNECTIONS:
      'https://connections-reporting.corp.amazon.com/#/home?currentView=ORGS&selectedTimeRangeId=static-1-month',
    POLICY_2_0: 'https://policy.a2z.com/',
    LABOR_SCHEDULING_TOOL: 'https://scheduling.amazon.com/',
    HRSC_CASE_METRICS:
      'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/e15aa502-36cc-43aa-ac62-9cc2ec0472c6/sheets/e15aa502-36cc-43aa-ac62-9cc2ec0472c6_6e1dfb97-e3ed-4ae5-8795-0720df85bb20',
    HAWKEYE_HMD:
      'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/efbda839-26fc-45bc-85e0-79b6af874aa3/sheets/efbda839-26fc-45bc-85e0-79b6af874aa3_fd324358-8075-4c39-89c0-6982f35cae14',
    RISE: 'https://rise.aka.amazon.com/overview',
    MAPLE_SELF_SERVICE_USER_PROFILE:
      'https://w.amazon.com/bin/view/FPDS/FPDS-DCX/DCX-Security/MapleMySupport/UserGuide/UserProfile/',
    CREATE_ACCESS_ISSUE_TICKET: 'https://t.corp.amazon.com/create/templates/a504176f-7a82-4add-821c-75819b387593',
    POLICY_BY_LOCATION: 'https://inside.hr.amazon.dev/us/en/employment/policies-global.html',
    POLICY_STYLE_GUIDE: 'https://w.amazon.com/bin/view/HR_Global_Content_Team/Style_Guide',
    POLICY_TEAM_WIKI: 'https://w.amazon.com/bin/view/PXT3-policy',
    REQUEST_NEW_POLICY: 'https://issues.amazon.com/issues/create?template=199b0334-87bd-4e05-8d6b-755e49d1cfc0',
    REQUEST_POLICY_CHANGE: 'https://form.asana.com/?k=ZuEAhmZPM8P2ymblBb9-kQ&d=8442528107068',
    ORIGAMI: 'https://live.origamirisk.com/Origami/IncidentEntry/Welcome',
    PXT_ONE_FAQ_WIKI: 'https://w.amazon.com/bin/view/PXT_Apps/HRSC/Features/PXT_One_Request/FAQs/',
    CLEAR_COOKIES_GUIDE: 'https://w.amazon.com/bin/view/HRIS/How-to-clear-browser-cache-and-cookies',
    NA_OPS_PXT_RESOURCE_HUB: 'https://w.amazon.com/bin/view/NA_Ops_PXT/Resource_Hub/',
    PXT_SUPPORT_RESOURCES: 'https://w.amazon.com/bin/view/PXT_Support',
    HR_SUPPORT_TOOLKIT: 'https://inside.hr.amazon.dev/us/en/toolkits/for-hr/toolkits/hr-support.html',
    VOA_EXPLORER: 'https://peopleinsightvoaexplorer.corp.amazon.com/explorer',
    AMAZON_HIRE: 'https://hire.amazon.com/',
    JOB_FINDER: 'https://jobfinder.amazon.com/',
    ICIMS_RECRUITING_REQUISITION_MANAGMENT:
      'https://amazon.icims.com/icims2/servlet/icims2?module=Root&action=login&sso=1&hashed=-1353432425',
    ERCI_INTAKE: 'https://w.amazon.com/bin/view/ERCI_WIKI/Home/ERCI_Intake_Process/',
    DEI_RESOURCES: 'https://w.amazon.com/bin/view/Gdeiofferingsupport',
    WWAS_TALENT_MANAGEMENT: 'https://w.amazon.com/bin/view/ConsumerTalentManagement/',
    PXT_ACADEMY: 'https://w.amazon.com/bin/view/GlobalOpsPXTAcademy/',
    WORKFORCE_DATA_MANAGEMENT_TOOL: 'https://wdmt.pxt.amazon.dev/',
    REQUEST_PXT_ALERT: 'https://issues.amazon.com/issues/create?template=9f007690-4d9c-42c5-9fb7-5f52bb3e29d8',
    REQUEST_COMMS_SUPPORT: 'https://issues.amazon.com/issues/create?template=3767f065-0c3b-4436-88bb-cf36a0d7dd4a',
    COMMS_SELF_SERVICE: 'https://w.amazon.com/bin/view/PXT3-comms/intake-and-support/self-service/',
    COMMS_TEAM_WIKI: 'https://w.amazon.com/bin/view/PXT3-comms/',
    CONTACT_PXT_COMMS: 'mailto:pxtcomms@amazon.com',
    CM_FRAMEWORK_AND_ESTTENSITALS_RESOURCES:
      'https://inside.amazon.com/en/toolkits/ChangeManagement/Pages/CM_Framework_Essentials.aspx',
    REQUEST_CM_SUPPORT: 'https://w.amazon.com/bin/view/PXT3-CM/CM_Resources/CM_Intake/',
    CHANGE_MANAGMENT_WIKI: 'https://w.amazon.com/bin/view/PXT3-CM/',
    CONTACT_CHANGE_MANAGEMENT: 'mailto:PXT3-Change@amazon.com',
    QUICKSIGHT_EMPLOYEE_DATA_SUMMARY:
      'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/2014b362-7b80-4cb9-9daa-c341e164ca79/sheets/2014b362-7b80-4cb9-9daa-c341e164ca79_6ea7dfdf-3f36-4933-94b1-c197855e0df3',
    QUICKSIGHT_SINGLE_EMPLOYEE:
      'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/d7cf06d9-65cb-4c7a-988a-72ae3bdbd78e/sheets/d7cf06d9-65cb-4c7a-988a-72ae3bdbd78e_50070724-25b8-4353-aeb9-3d55f4735955?#',
    SAY_HI: 'https://sayhi.pxt.amazon.dev/',
    LINGUISTIC_ACCESSIBILITY_WIKI: 'https://w.amazon.com/bin/view/LinguisticAccessibilityToolkit/MLCResources/',
  },
  [STAGE.ONEBOX]: {
    REINSTATEMENT_FORM: 'https://console.harmony.a2z.com/exits-ci/ReinstatementIntake',
    NA_OPS_PXT_STANDARD_WORK_FIELD_GUIDES: 'https://atoz.amazon.work/pages/na-ops-pxt/Standard-Work-Field-Guides',
    REHIRE_ELIGIBILITY_ENGINE: 'https://ree.chip.people-tech.a2z.com/',
    EMPLOYEE_DOCUMENTS_PORTAL: 'https://docs.amazon.com/admin/us/dashboard/documents',
    LiveSupportLOA:
      'https://atoz.amazon.work/myhr/livehelp/landing?category=HR+Support+on+behalf+of+an+employee&type=Chat+with+Leave+and+Accommodations',
    LiveSupportHRSupport:
      'https://atoz.amazon.work/myhr/livehelp/landing?category=HR+Support+on+behalf+of+an+employee&type=Chat+with+HR+Support',
    LiveSupportPanoCreateCase: 'https://atoz.amazon.work/myhr/livehelp/landing?category=Test&type=HRSC',
    ManageMyLeave: 'https://atoz.amazon.work/myhr/category/time,_leave_of_absence_and_accommodation',
    PEOPLE_PORTAL: 'https://peopleportal.hr.corp.amazon.com/',
    PHONE_TOOL: 'https://phonetool.amazon.com/users/',
    HR_KEY: 'https://hrkey.amazon.com/',
    ALLSEC: 'https://www.allsechro.com/AmazonSSO/',
    SIM_T: 'https://t.corp.amazon.com/',
    SMART_FORMS: 'https://smartforms.corp.amazon.com/',
    QUICK_SIGHT: 'https://wisdom.corp.amazon.com/quicksight',
    CLARITY: 'https://clarity.people-insight.a2z.com/',
    PANORAMA: 'https://amazon-hr.my.salesforce.com/',
    BEXT_DASHBOARD:
      'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/e7c88c97-a921-4e74-9d24-d4884cc06f10/sheets/e7c88c97-a921-4e74-9d24-d4884cc06f10_7f1c1d1c-1375-4b10-90ee-d4fcab29f7b5',
    HELP_CREATE_ISSUE: 'https://sim.amazon.com/issues/create?template=50c2f318-6858-4623-ab68-8e0c13e0f18a',
    AMAZON_LOGO: 'https://hrsc.pxt.amazon.dev/assets/AMAZON_LOGO.png', //TODO Remove Amazon LOGO
    HRSC_LOGO: 'https://hrsc.pxt.amazon.dev/assets/HRSC_Logo_RV_V2.png',
    SUBMIT_FEEDBACK: 'https://survey.fieldsense.whs.amazon.dev/survey/252cfef4-b4ae-46bf-99c4-6450708c0b60',
    CAMP: 'https://camp.corp.amazon.com/',
    LENEL: 'http://badgescanreport.corp.amazon.com/',
    FCLM: 'https://fclm-portal.amazon.com/',
    WIKI: 'https://w.amazon.com/bin/view/Main/',
    HR_TOOLKIT: 'https://inside.hr.amazon.dev/content/inside/us/en/toolkits/for-hr.html',
    EMPLOYEE_POLICIES: 'https://inside.hr.amazon.dev/us/en/employment/policies-global.html',
    DLS: 'http://dls.idp.amazon-corp.com/',
    IVY: 'https://ivy.talent.a2z.com/',
    ADAPT: 'https://adapt-iad.amazon.com/',
    TCUI_PORTAL: 'https://weui.workevents.a2z.com/teams-timecard/',
    INTERNAL_SERVER_ERROR_IMAGE: 'https://hrsc.pxt.amazon.dev/assets/FAILURE_ERROR_IMAGE.png',
    ACCESS_DENIED_PAGE_IMAGE: 'https://hrsc.pxt.amazon.dev/assets/output.png',
    PAGE_NOT_FOUND_IMAGE: 'https://hrsc.pxt.amazon.dev/assets/404.png',
    SUCCESS_IMAGE: 'https://hrsc.pxt.amazon.dev/assets/success.png',
    ADMIN_DIRECTORY: 'https://admindirectory.corp.amazon.com/users/',
    CUBE_NEWS: 'https://news.cube.pxt.amazon.dev/',
    KNOWLEDGE_BASE: 'https://hrsc-kb.pxt.amazon.dev/etc/amazon-hr/paths/search-proxy.html',
    EXACT: 'https://exactinvestigation.my.salesforce.com/',
    UMBRELLA: 'https://iad.umbrella.amazon.dev/sdl/dashboard',
    ONBASE: 'https://amazon.onbaseonline.com/idpappnet/',
    WELCOME_BANNER_IMAGE: 'https://hrsc.pxt.amazon.dev/assets/WELCOME_BANNER_IMAGE.png',
    SUPPORT: 'https://hrsc.pxt.amazon.dev/assets/SUPPORT.png',
    GSS: 'https://na.labor-admin.gsf.a2z.com/scheduling',
    ATOZ_MY_HR: 'https://atoz.amazon.work/myhr',
    MAPLE_MY_SUPPORT_ASSIGNMENTS: 'https://maple-admin-prod.corp.amazon.com/my-support-assignments/populations',
    DALI_LINK_PREFIX: 'https://amazonloaclaimvantage.lightning.force.com/lightning/r/',
    PXT_ONE_QUICKSIGHT_DASHBOARD:
      'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/80e8a75b-555e-4c17-9f1a-ff8f5962c4f8',
    PXT_ONE_NO_ACCESS_IMAGE: 'https://hrsc.pxt.amazon.dev/assets/lock.svg',
    ENGAGE: 'https://na.engage.amazon.dev/',
    CONNECTIONS:
      'https://connections-reporting.corp.amazon.com/#/home?currentView=ORGS&selectedTimeRangeId=static-1-month',
    POLICY_2_0: 'https://policy.a2z.com/',
    LABOR_SCHEDULING_TOOL: 'https://scheduling.amazon.com/',
    HRSC_CASE_METRICS:
      'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/e15aa502-36cc-43aa-ac62-9cc2ec0472c6/sheets/e15aa502-36cc-43aa-ac62-9cc2ec0472c6_6e1dfb97-e3ed-4ae5-8795-0720df85bb20',
    HAWKEYE_HMD:
      'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/efbda839-26fc-45bc-85e0-79b6af874aa3/sheets/efbda839-26fc-45bc-85e0-79b6af874aa3_fd324358-8075-4c39-89c0-6982f35cae14',
    RISE: 'https://rise.aka.amazon.com/overview',
    MAPLE_SELF_SERVICE_USER_PROFILE:
      'https://w.amazon.com/bin/view/FPDS/FPDS-DCX/DCX-Security/MapleMySupport/UserGuide/UserProfile/',
    CREATE_ACCESS_ISSUE_TICKET: 'https://t.corp.amazon.com/create/templates/a504176f-7a82-4add-821c-75819b387593',
    POLICY_BY_LOCATION: 'https://inside.hr.amazon.dev/us/en/employment/policies-global.html',
    POLICY_STYLE_GUIDE: 'https://w.amazon.com/bin/view/HR_Global_Content_Team/Style_Guide',
    POLICY_TEAM_WIKI: 'https://w.amazon.com/bin/view/PXT3-policy',
    REQUEST_NEW_POLICY: 'https://issues.amazon.com/issues/create?template=199b0334-87bd-4e05-8d6b-755e49d1cfc0',
    REQUEST_POLICY_CHANGE: 'https://form.asana.com/?k=ZuEAhmZPM8P2ymblBb9-kQ&d=8442528107068',
    ORIGAMI: 'https://live.origamirisk.com/Origami/IncidentEntry/Welcome',
    PXT_ONE_FAQ_WIKI: 'https://w.amazon.com/bin/view/PXT_Apps/HRSC/Features/PXT_One_Request/FAQs/',
    CLEAR_COOKIES_GUIDE: 'https://w.amazon.com/bin/view/HRIS/How-to-clear-browser-cache-and-cookies',
    NA_OPS_PXT_RESOURCE_HUB: 'https://w.amazon.com/bin/view/NA_Ops_PXT/Resource_Hub/',
    PXT_SUPPORT_RESOURCES: 'https://w.amazon.com/bin/view/PXT_Support',
    HR_SUPPORT_TOOLKIT: 'https://inside.hr.amazon.dev/us/en/toolkits/for-hr/toolkits/hr-support.html',
    VOA_EXPLORER: 'https://peopleinsightvoaexplorer.corp.amazon.com/explorer',
    AMAZON_HIRE: 'https://hire.amazon.com/',
    JOB_FINDER: 'https://jobfinder.amazon.com/',
    ICIMS_RECRUITING_REQUISITION_MANAGMENT:
      'https://amazon.icims.com/icims2/servlet/icims2?module=Root&action=login&sso=1&hashed=-1353432425',
    ERCI_INTAKE: 'https://w.amazon.com/bin/view/ERCI_WIKI/Home/ERCI_Intake_Process/',
    DEI_RESOURCES: 'https://w.amazon.com/bin/view/Gdeiofferingsupport',
    WWAS_TALENT_MANAGEMENT: 'https://w.amazon.com/bin/view/ConsumerTalentManagement/',
    PXT_ACADEMY: 'https://w.amazon.com/bin/view/GlobalOpsPXTAcademy/',
    WORKFORCE_DATA_MANAGEMENT_TOOL: 'https://wdmt.pxt.amazon.dev/',
    REQUEST_PXT_ALERT: 'https://issues.amazon.com/issues/create?template=9f007690-4d9c-42c5-9fb7-5f52bb3e29d8',
    REQUEST_COMMS_SUPPORT: 'https://issues.amazon.com/issues/create?template=3767f065-0c3b-4436-88bb-cf36a0d7dd4a',
    COMMS_SELF_SERVICE: 'https://w.amazon.com/bin/view/PXT3-comms/intake-and-support/self-service/',
    COMMS_TEAM_WIKI: 'https://w.amazon.com/bin/view/PXT3-comms/',
    CONTACT_PXT_COMMS: 'mailto:pxtcomms@amazon.com',
    CM_FRAMEWORK_AND_ESTTENSITALS_RESOURCES:
      'https://inside.amazon.com/en/toolkits/ChangeManagement/Pages/CM_Framework_Essentials.aspx',
    REQUEST_CM_SUPPORT: 'https://w.amazon.com/bin/view/PXT3-CM/CM_Resources/CM_Intake/',
    CHANGE_MANAGMENT_WIKI: 'https://w.amazon.com/bin/view/PXT3-CM/',
    CONTACT_CHANGE_MANAGEMENT: 'mailto:PXT3-Change@amazon.com',
    QUICKSIGHT_EMPLOYEE_DATA_SUMMARY:
      'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/2014b362-7b80-4cb9-9daa-c341e164ca79/sheets/2014b362-7b80-4cb9-9daa-c341e164ca79_6ea7dfdf-3f36-4933-94b1-c197855e0df3',
    QUICKSIGHT_SINGLE_EMPLOYEE:
      'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/d7cf06d9-65cb-4c7a-988a-72ae3bdbd78e/sheets/d7cf06d9-65cb-4c7a-988a-72ae3bdbd78e_50070724-25b8-4353-aeb9-3d55f4735955?#',
    SAY_HI: 'https://sayhi.pxt.amazon.dev/',
    LINGUISTIC_ACCESSIBILITY_WIKI: 'https://w.amazon.com/bin/view/LinguisticAccessibilityToolkit/MLCResources/',
  },
  [STAGE.PROD]: {
    REINSTATEMENT_FORM: 'https://console.harmony.a2z.com/exits-ci/ReinstatementIntake',
    NA_OPS_PXT_STANDARD_WORK_FIELD_GUIDES: 'https://atoz.amazon.work/pages/na-ops-pxt/Standard-Work-Field-Guides',
    REHIRE_ELIGIBILITY_ENGINE: 'https://ree.chip.people-tech.a2z.com/',
    EMPLOYEE_DOCUMENTS_PORTAL: 'https://docs.amazon.com/admin/us/dashboard/documents',
    LiveSupportLOA:
      'https://atoz.amazon.work/myhr/livehelp/landing?category=HR+Support+on+behalf+of+an+employee&type=Chat+with+Leave+and+Accommodations',
    LiveSupportHRSupport:
      'https://atoz.amazon.work/myhr/livehelp/landing?category=HR+Support+on+behalf+of+an+employee&type=Chat+with+HR+Support',
    LiveSupportPanoCreateCase: 'https://atoz.amazon.work/myhr/livehelp/landing?category=Test&type=HRSC',
    ManageMyLeave: 'https://atoz.amazon.work/myhr/category/time,_leave_of_absence_and_accommodation',
    PEOPLE_PORTAL: 'https://peopleportal.hr.corp.amazon.com/',
    PHONE_TOOL: 'https://phonetool.amazon.com/users/',
    HR_KEY: 'https://hrkey.amazon.com/',
    ALLSEC: 'https://www.allsechro.com/AmazonSSO/',
    SIM_T: 'https://t.corp.amazon.com/',
    SMART_FORMS: 'https://smartforms.corp.amazon.com/',
    QUICK_SIGHT: 'https://wisdom.corp.amazon.com/quicksight',
    CLARITY: 'https://clarity.people-insight.a2z.com/',
    PANORAMA: 'https://amazon-hr.my.salesforce.com/',
    BEXT_DASHBOARD:
      'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/e7c88c97-a921-4e74-9d24-d4884cc06f10/sheets/e7c88c97-a921-4e74-9d24-d4884cc06f10_7f1c1d1c-1375-4b10-90ee-d4fcab29f7b5',
    HELP_CREATE_ISSUE: 'https://sim.amazon.com/issues/create?template=50c2f318-6858-4623-ab68-8e0c13e0f18a',
    AMAZON_LOGO: 'https://hrsc.pxt.amazon.dev/assets/AMAZON_LOGO.png', //TODO Remove Amazon LOGO
    HRSC_LOGO: 'https://hrsc.pxt.amazon.dev/assets/HRSC_Logo_RV_V2.png',
    SUBMIT_FEEDBACK: 'https://survey.fieldsense.whs.amazon.dev/survey/252cfef4-b4ae-46bf-99c4-6450708c0b60',
    CAMP: 'https://camp.corp.amazon.com/',
    LENEL: 'http://badgescanreport.corp.amazon.com/',
    FCLM: 'https://fclm-portal.amazon.com/',
    WIKI: 'https://w.amazon.com/bin/view/Main/',
    HR_TOOLKIT: 'https://inside.hr.amazon.dev/content/inside/us/en/toolkits/for-hr.html',
    EMPLOYEE_POLICIES: 'https://inside.hr.amazon.dev/us/en/employment/policies-global.html',
    DLS: 'http://dls.idp.amazon-corp.com/',
    IVY: 'https://ivy.talent.a2z.com/',
    ADAPT: 'https://adapt-iad.amazon.com/',
    TCUI_PORTAL: 'https://weui.workevents.a2z.com/teams-timecard/',
    INTERNAL_SERVER_ERROR_IMAGE: 'https://hrsc.pxt.amazon.dev/assets/FAILURE_ERROR_IMAGE.png',
    ACCESS_DENIED_PAGE_IMAGE: 'https://hrsc.pxt.amazon.dev/assets/output.png',
    PAGE_NOT_FOUND_IMAGE: 'https://hrsc.pxt.amazon.dev/assets/404.png',
    SUCCESS_IMAGE: 'https://hrsc.pxt.amazon.dev/assets/success.png',
    ADMIN_DIRECTORY: 'https://admindirectory.corp.amazon.com/users/',
    CUBE_NEWS: 'https://news.cube.pxt.amazon.dev/',
    KNOWLEDGE_BASE: 'https://hrsc-kb.pxt.amazon.dev/etc/amazon-hr/paths/search-proxy.html',
    EXACT: 'https://exactinvestigation.my.salesforce.com/',
    UMBRELLA: 'https://iad.umbrella.amazon.dev/sdl/dashboard',
    ONBASE: 'https://amazon.onbaseonline.com/idpappnet/',
    WELCOME_BANNER_IMAGE: 'https://hrsc.pxt.amazon.dev/assets/WELCOME_BANNER_IMAGE.png',
    SUPPORT: 'https://hrsc.pxt.amazon.dev/assets/SUPPORT.png',
    GSS: 'https://na.labor-admin.gsf.a2z.com/scheduling',
    ATOZ_MY_HR: 'https://atoz.amazon.work/myhr',
    MAPLE_MY_SUPPORT_ASSIGNMENTS: 'https://maple-admin-prod.corp.amazon.com/my-support-assignments/populations',
    DALI_LINK_PREFIX: 'https://amazonloaclaimvantage.lightning.force.com/lightning/r/',
    PXT_ONE_QUICKSIGHT_DASHBOARD:
      'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/80e8a75b-555e-4c17-9f1a-ff8f5962c4f8',
    PXT_ONE_NO_ACCESS_IMAGE: 'https://hrsc.pxt.amazon.dev/assets/lock.svg',
    ENGAGE: 'https://na.engage.amazon.dev/',
    CONNECTIONS:
      'https://connections-reporting.corp.amazon.com/#/home?currentView=ORGS&selectedTimeRangeId=static-1-month',
    POLICY_2_0: 'https://policy.a2z.com/',
    LABOR_SCHEDULING_TOOL: 'https://scheduling.amazon.com/',
    HRSC_CASE_METRICS:
      'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/e15aa502-36cc-43aa-ac62-9cc2ec0472c6/sheets/e15aa502-36cc-43aa-ac62-9cc2ec0472c6_6e1dfb97-e3ed-4ae5-8795-0720df85bb20',
    HAWKEYE_HMD:
      'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/efbda839-26fc-45bc-85e0-79b6af874aa3/sheets/efbda839-26fc-45bc-85e0-79b6af874aa3_fd324358-8075-4c39-89c0-6982f35cae14',
    RISE: 'https://rise.aka.amazon.com/overview',
    MAPLE_SELF_SERVICE_USER_PROFILE:
      'https://w.amazon.com/bin/view/FPDS/FPDS-DCX/DCX-Security/MapleMySupport/UserGuide/UserProfile/',
    CREATE_ACCESS_ISSUE_TICKET: 'https://t.corp.amazon.com/create/templates/a504176f-7a82-4add-821c-75819b387593',
    POLICY_BY_LOCATION: 'https://inside.hr.amazon.dev/us/en/employment/policies-global.html',
    POLICY_STYLE_GUIDE: 'https://w.amazon.com/bin/view/HR_Global_Content_Team/Style_Guide',
    POLICY_TEAM_WIKI: 'https://w.amazon.com/bin/view/PXT3-policy',
    REQUEST_NEW_POLICY: 'https://issues.amazon.com/issues/create?template=199b0334-87bd-4e05-8d6b-755e49d1cfc0',
    REQUEST_POLICY_CHANGE: 'https://form.asana.com/?k=ZuEAhmZPM8P2ymblBb9-kQ&d=8442528107068',
    ORIGAMI: 'https://live.origamirisk.com/Origami/IncidentEntry/Welcome',
    PXT_ONE_FAQ_WIKI: 'https://w.amazon.com/bin/view/PXT_Apps/HRSC/Features/PXT_One_Request/FAQs/',
    CLEAR_COOKIES_GUIDE: 'https://w.amazon.com/bin/view/HRIS/How-to-clear-browser-cache-and-cookies',
    NA_OPS_PXT_RESOURCE_HUB: 'https://w.amazon.com/bin/view/NA_Ops_PXT/Resource_Hub/',
    PXT_SUPPORT_RESOURCES: 'https://w.amazon.com/bin/view/PXT_Support',
    HR_SUPPORT_TOOLKIT: 'https://inside.hr.amazon.dev/us/en/toolkits/for-hr/toolkits/hr-support.html',
    VOA_EXPLORER: 'https://peopleinsightvoaexplorer.corp.amazon.com/explorer',
    AMAZON_HIRE: 'https://hire.amazon.com/',
    JOB_FINDER: 'https://jobfinder.amazon.com/',
    ICIMS_RECRUITING_REQUISITION_MANAGMENT:
      'https://amazon.icims.com/icims2/servlet/icims2?module=Root&action=login&sso=1&hashed=-1353432425',
    ERCI_INTAKE: 'https://w.amazon.com/bin/view/ERCI_WIKI/Home/ERCI_Intake_Process/',
    DEI_RESOURCES: 'https://w.amazon.com/bin/view/Gdeiofferingsupport',
    WWAS_TALENT_MANAGEMENT: 'https://w.amazon.com/bin/view/ConsumerTalentManagement/',
    PXT_ACADEMY: 'https://w.amazon.com/bin/view/GlobalOpsPXTAcademy/',
    WORKFORCE_DATA_MANAGEMENT_TOOL: 'https://wdmt.pxt.amazon.dev/',
    REQUEST_PXT_ALERT: 'https://issues.amazon.com/issues/create?template=9f007690-4d9c-42c5-9fb7-5f52bb3e29d8',
    REQUEST_COMMS_SUPPORT: 'https://issues.amazon.com/issues/create?template=3767f065-0c3b-4436-88bb-cf36a0d7dd4a',
    COMMS_SELF_SERVICE: 'https://w.amazon.com/bin/view/PXT3-comms/intake-and-support/self-service/',
    COMMS_TEAM_WIKI: 'https://w.amazon.com/bin/view/PXT3-comms/',
    CONTACT_PXT_COMMS: 'mailto:pxtcomms@amazon.com',
    CM_FRAMEWORK_AND_ESTTENSITALS_RESOURCES:
      'https://inside.amazon.com/en/toolkits/ChangeManagement/Pages/CM_Framework_Essentials.aspx',
    REQUEST_CM_SUPPORT: 'https://w.amazon.com/bin/view/PXT3-CM/CM_Resources/CM_Intake/',
    CHANGE_MANAGMENT_WIKI: 'https://w.amazon.com/bin/view/PXT3-CM/',
    CONTACT_CHANGE_MANAGEMENT: 'mailto:PXT3-Change@amazon.com',
    QUICKSIGHT_EMPLOYEE_DATA_SUMMARY:
      'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/2c11a58a-4d64-40d3-8937-50759c122245/sheets/2c11a58a-4d64-40d3-8937-50759c122245_d1d2e0eb-2a0a-4711-b2d6-593b2887d5c5',
    QUICKSIGHT_SINGLE_EMPLOYEE:
      'https://us-east-1.quicksight.aws.amazon.com/sn/dashboards/0c44c743-7167-42a3-a61e-3a7bf13d0589/sheets/0c44c743-7167-42a3-a61e-3a7bf13d0589_1d9273f3-f5ba-4125-8b5b-7c031dc8de9d',
    SAY_HI: 'https://sayhi.pxt.amazon.dev/',
    LINGUISTIC_ACCESSIBILITY_WIKI: 'https://w.amazon.com/bin/view/LinguisticAccessibilityToolkit/MLCResources/',
  },
};

/*
 * Constants for MultiTab-view page urls
 */
export const CASE_DETAILS_ROUTER_LINK_PREFIX = '/caseDetails/';
export const TASK_DETAILS_ROUTER_LINK_PREFIX = '/taskDetails';
export const EMPLOYEE_PROFILE_ROUTER_LINK_PREFIX = '/employeeDirectory/';
export const EXTERNAL_CONTACT_INFO_ROUTER_LINK_PREFIX = '/externalContactInfo/';
export const EXTERNAL_CONTACT_DIRECTORY_ROUTER_LINK_PREFIX = '/externalDirectory/';
export const CREATE_CASE_PREFIX = '/createCase/';
export const CLONE_CASE_PREFIX = '/cloneCase/';
export const COE_SUPPORT_CASE_DETAILS_PREFIX = '/support/supportCaseDetails/';
export const CUBE_NEWS_DETAIL_PREFIX = '/news/content/';
export const LEAVE_STATUS_FEED_PREFIX = '/LeaveStatusFeed';
export const SITE_WALK_UP_CREATE_CASE_PREFIX = '/siteWalkUp/createCase/';
export const SITE_WALK_UP_SUPPORT_PREFIX = '/siteWalkUp/support';
export const SEVERANCE_PREFIX = '/terminations/severances';
export const TEAM_PREFERENCE_PREFIX = '/teamPreferences';
export const USER_PREFERENCE_PREFIX = '/userPreferences';
export const TERMINATION_APPROVAL_PREFIX = '/terminationApproval';
export const TRANSCRIBE_INTERVIEW_PREFIX = '/transcribe';
export const AGENTIC_WORKFLOWS_PREFIX = '/agenticWorkflows';
export const FEEDBACK_DETAILS_PREFIX = '/employeeDirectory';
export const AGENT_SKILLS_PREFIX = '/agentSkills';
export const WFR_PROJECT_PREFIX = '/workforceReduction/projects';
export const WFR_CREATE_PROJECT_PREFIX = '/workforceReduction/projects/new';
